import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Phone,
  Globe,
  Mail,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import RatingPentagon from "./RatingPentagon";
import { photoUrl } from "../config";

const SLIDE_VARIANTS = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

function HotelCard({ hotel, onPhotoClick }) {
  const navigate = useNavigate();
  const [imgIdx, setImgIdx] = useState(0);
  const [slideDir, setSlideDir] = useState(0);
  const images =
    hotel.photos?.length > 0
      ? hotel.photos.map((p) => `${photoUrl}/${hotel.photo_url}/hotel/${p}.jpg`)
      : ["/empty.jpg"];
  const total = images.length;

  const prev = (e) => {
    e.stopPropagation();
    setSlideDir(-1);
    setImgIdx((i) => (i - 1 + total) % total);
  };

  const next = (e) => {
    e.stopPropagation();
    setSlideDir(1);
    setImgIdx((i) => (i + 1) % total);
  };

  const selectDot = (e, i) => {
    e.stopPropagation();
    setSlideDir(i > imgIdx ? 1 : -1);
    setImgIdx(i);
  };

  // Клик по фото — открывает просмотрщик, НЕ переходит по карточке
  const handlePhotoClick = (e) => {
    e.stopPropagation();
    onPhotoClick?.(images, imgIdx);
  };

  // Обработчик клика по карточке
  const handleCardClick = () => {
    if (hotel.url) {
      navigate(hotel.url, { state: { from: "sprav" } });
    }
  };

  // Обработчик клика по кнопке "Выбрать"
  const handleSelectClick = (e) => {
    e.stopPropagation();
    if (hotel.url) {
      navigate(hotel.url, { state: { from: "sprav" } });
    }
  };

  // Обработчики для контактов (чтобы не переходить по карточке)
  const handleContactClick = (e, type, value) => {
    e.stopPropagation();
    if (type === "phone" && value) {
      window.location.href = `tel:${value}`;
    } else if (type === "email" && value) {
      window.location.href = `mailto:${value}`;
    } else if (type === "website" && value) {
      window.open(value, "_blank");
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="relative bg-white rounded-xl shadow hover:shadow-lg transition-all overflow-hidden flex flex-col sm:flex-row border border-gray-100 h-40 sm:h-48 cursor-pointer group"
    >
      {/* Image */}
      <div
        className="relative w-full sm:w-56 h-40 sm:h-auto flex-shrink-0 group overflow-hidden cursor-pointer"
        onClick={handlePhotoClick}
      >
        <AnimatePresence initial={false} custom={slideDir}>
          <motion.img
            key={imgIdx}
            src={images[imgIdx]}
            alt={hotel.name}
            custom={slideDir}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <span className="absolute top-2 left-2 bg-white/90 text-gray-700 text-xs font-medium px-2 py-0.5 rounded-full shadow-sm z-10">
          {hotel?.template || "Отель"}
        </span>
        {total > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-opacity z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-opacity z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 max-w-[90%] overflow-hidden px-2 z-10">
              {images.map((_, i) => {
                const shouldShow =
                  total <= 7 ||
                  i === 0 ||
                  i === total - 1 ||
                  Math.abs(i - imgIdx) <= 2;

                if (!shouldShow) return null;

                return (
                  <button
                    key={i}
                    onClick={(e) => selectDot(e, i)}
                    className={`flex-shrink-0 rounded-full transition-all ${
                      i === imgIdx
                        ? "w-3 h-1.5 bg-white"
                        : "w-1.5 h-1.5 bg-white/50 hover:bg-white/70"
                    }`}
                  />
                );
              })}
            </div>
            <div className="absolute bottom-1.5 right-1.5 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-md z-10">
              {imgIdx + 1} / {total}
            </div>
          </>
        )}
      </div>

      {/* Rating pentagon + reviews */}
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col items-end gap-1 z-10">
        {hotel?.meta_rating > 0 && (
          <RatingPentagon rating={hotel.meta_rating} size="sm" />
        )}
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {hotel.reviews_count || 0} отзывов
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 flex flex-col justify-between gap-2 min-w-0">
        <div>
          <h3 className="font-semibold text-gray-900 text-base leading-tight">
            {hotel.name || `#${hotel.id}`}
          </h3>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {hotel.city ? `${hotel.city}, ` : ""}
              {hotel.address?.split(",")?.[0] || ""}
            </span>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mt-8">
            {hotel.popular_services?.join(" · ") || ""}
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          {/* Contacts */}
          {(!!hotel?.owner_confirmed || hotel?.contacts_is_active === true) && (
            <div className="flex items-center gap-2">
              {hotel.phone && (
                <button
                  onClick={(e) =>
                    handleContactClick(e, "phone", hotel.phone?.[0])
                  }
                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" />
                </button>
              )}
              {hotel.website && (
                <button
                  onClick={(e) =>
                    handleContactClick(e, "website", hotel.website)
                  }
                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                >
                  <Globe className="w-3.5 h-3.5" />
                </button>
              )}
              {hotel.email && (
                <button
                  onClick={(e) => handleContactClick(e, "email", hotel.email)}
                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          {/* Price */}
          {hotel.min_price > 0 && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <span className="text-xs text-gray-400">от </span>
                <span className="text-base font-bold text-gray-900">
                  {hotel.min_price.toLocaleString("ru-RU")} ₽
                </span>
                <span className="text-xs text-gray-400"> / ночь</span>
              </div>
              <button
                onClick={handleSelectClick}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
              >
                Выбрать
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HotelCard;
